import { IconLogOut, IconMenu, IconSearch } from '@developerz/ui';
import { useLocation } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgFromPath } from '../lib/org';
import { ADMIN_NAV, currentRailItem, NAV } from '../lib/rail';
import { signOut } from '../lib/sign-out';
import { NAV_DRAWER_ID } from './AppSidebar';
import { LanguagePicker } from './LanguagePicker';
import { McpChip } from './McpChip';
import { NotificationBell } from './NotificationBell';
import { PushToggle } from './PushToggle';
import { ThemeToggle } from './ThemeToggle';
import { Button, buttonClass } from './ui/Button';

// The sticky topbar (the canvas topbar, 08 / APP): drawer toggle + the mono path
// crumb on the left (`/org/<org>` — the org segment IS the tenant — then the
// section), the MCP chip, ⌘K, the language picker and the session controls on
// the right. Split out of <AppShell/> when the command palette's trigger joined
// them.

/** The palette chord as the badge shows it — and so as the trigger's name says it. */
const PALETTE_CHORD = '⌘K';

export function AppTopbar(props: {
  readonly adminHost: boolean;
  /** The shell's org-aware href resolver — the breadcrumb matches against it. */
  readonly resolve: (href: string) => string;
  readonly navOpen: boolean;
  readonly onOpenNav: () => void;
  readonly onOpenPalette: () => void;
  readonly onOpenShortcuts: () => void;
}): JSX.Element {
  const location = useLocation();

  // The section: the rail row the reader is on (`currentRailItem`, the rule the
  // sidebar lights its row by). Nothing when no row matches — a guessed section
  // is a wrong one.
  const section = (): string | undefined => {
    const hit = props.adminHost
      ? currentRailItem(location.pathname, ADMIN_NAV, (href) => href)
      : currentRailItem(location.pathname, NAV, props.resolve);
    return hit === undefined ? undefined : t(hit.label);
  };
  // The tenant as the URL spells it. Off `/org/…` (the admin radar, a legacy
  // redirect) there is no org, so the crumb is the section alone.
  const path = (): string | undefined => {
    const org = orgFromPath(location.pathname);
    return org === null ? undefined : `/org/${org}`;
  };

  return (
    <header class="app-topbar">
      <div class="app-topbar__lead">
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'icon' })} app-topbar__menu`}
          aria-label={t('nav.open_menu')}
          aria-expanded={props.navOpen}
          aria-controls={NAV_DRAWER_ID}
          onClick={props.onOpenNav}
        >
          <IconMenu size="1rem" />
        </button>
        <p class="app-topbar__crumb">
          <Show when={path()}>{(p) => <span class="app-topbar__path">{p()}</span>}</Show>
          <Show when={path() !== undefined && section() !== undefined}>
            <span class="app-topbar__crumb-sep" aria-hidden="true">
              /
            </span>
          </Show>
          <Show when={section()}>{(name) => <b>{name()}</b>}</Show>
        </p>
      </div>
      <div class="app-topbar__controls">
        <Show when={!props.adminHost}>
          <McpChip resolve={props.resolve} />
        </Show>
        {/* The palette's only visible affordance. A keyboard-only feature is one
            nobody finds, and this is also the pointer door onto it — the parity
            rule cuts both ways. `aria-keyshortcuts` states the chord that AT
            reads out; the <kbd> states it for everyone else. The badge IS in the
            accessible name: WCAG 2.5.3 (label in name) — enforced by axe-core's
            `label-content-name-mismatch` from 4.14 — wants what is shown to be
            sayable, so the name is "Search ⌘K" even though AT that also announces
            `aria-keyshortcuts` reads the chord twice. One constant feeds both. */}
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'sm' })} app-topbar__search`}
          aria-label={t('palette.trigger', { chord: PALETTE_CHORD })}
          aria-keyshortcuts="Meta+K Control+K"
          onClick={props.onOpenPalette}
        >
          <IconSearch size="0.875rem" />
          <kbd class="app-topbar__kbd">{PALETTE_CHORD}</kbd>
        </button>
        {/* The `?` sheet's only visible affordance — the same parity rule, which
            the sheet went without: it was reachable by guessing one character
            and by nothing else. The badge IS the key, so the control teaches the
            chord it fires rather than needing a second badge to explain it. */}
        <button
          type="button"
          class={`${buttonClass({ variant: 'ghost', size: 'icon-sm' })} app-topbar__shortcuts`}
          aria-label={t('shortcuts.trigger')}
          aria-keyshortcuts="?"
          onClick={props.onOpenShortcuts}
        >
          <kbd class="app-topbar__kbd">?</kbd>
        </button>
        <NotificationBell />
        <PushToggle />
        <LanguagePicker id="topbar-locale" class="app-topbar__locale" />
        <ThemeToggle />
        <Button variant="ghost" size="sm" onClick={() => void signOut()}>
          <IconLogOut size="0.875rem" />
          {t('session.sign_out')}
        </Button>
      </div>
    </header>
  );
}
